import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Key, Plus, Copy, CheckCircle, Ban, Loader2, Clock, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-CL', { timeZone: 'America/Santiago', dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

export function ApiKeysCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null); // { name, api_key, key_preview, created_at }
  const [copied, setCopied] = useState(false);

  const [revokingId, setRevokingId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/api-keys`, { withCredentials: true });
      setItems(data.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo cargar la lista de API Keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/api-keys`,
        { name },
        { withCredentials: true }
      );
      setCreatedKey(data);
      setNewName('');
      setCreateOpen(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo crear la API Key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id, name) => {
    if (!window.confirm(`¿Revocar la API Key "${name}"?\n\nLa clave dejará de funcionar inmediatamente. La acción no se puede deshacer (no se elimina del historial).`)) {
      return;
    }
    setRevokingId(id);
    try {
      await axios.post(`${API_URL}/api/admin/api-keys/${id}/revoke`, {}, { withCredentials: true });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo revocar la API Key');
    } finally {
      setRevokingId(null);
    }
  };

  const copyKey = async () => {
    if (!createdKey?.api_key) return;
    try {
      await navigator.clipboard.writeText(createdKey.api_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Card className="bg-white rounded-xl border border-[#E8E5DD]" data-testid="api-keys-card">
      <CardHeader className="flex flex-row items-start sm:items-center justify-between gap-3 flex-wrap">
        <div>
          <CardTitle className="text-xl text-[#1A1C18] flex items-center gap-2">
            <Key size={22} className="text-[#8B5CF6]" />
            API Keys
          </CardTitle>
          <p className="text-sm text-[#6A736B] mt-1">
            Credenciales para integraciones externas de solo lectura (TerreMetrics u otros). Guardadas como SHA-256 — la clave en texto plano se muestra una única vez al crearla.
          </p>
        </div>
        <Button
          onClick={() => { setNewName(''); setCreateOpen(true); }}
          className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
          data-testid="api-keys-create-btn"
        >
          <Plus size={16} className="mr-2" />
          Crear API Key
        </Button>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-3 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 flex items-center gap-2">
            <AlertTriangle size={16} /> {error}
          </div>
        )}
        {loading ? (
          <div className="py-8 text-center text-sm text-[#6A736B] flex items-center justify-center gap-2">
            <Loader2 className="animate-spin" size={16} /> Cargando…
          </div>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-[#6A736B]">
            Aún no has creado ninguna API Key. Haz clic en “Crear API Key” para generar la primera.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="api-keys-table">
              <thead>
                <tr className="text-left text-[#6A736B] border-b border-[#E8E5DD] bg-[#FDFBF7]">
                  <th className="py-2.5 px-3 font-semibold">Nombre</th>
                  <th className="py-2.5 px-3 font-semibold">Prefijo</th>
                  <th className="py-2.5 px-3 font-semibold">Creada</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Uso</th>
                  <th className="py-2.5 px-3 font-semibold">Último uso</th>
                  <th className="py-2.5 px-3 font-semibold">Estado</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((k) => (
                  <tr key={k.id} className="border-b border-[#E8E5DD]/60 hover:bg-[#FDFBF7]" data-testid={`api-keys-row-${k.id}`}>
                    <td className="py-2.5 px-3 font-semibold text-[#1A1C18]">{k.name}</td>
                    <td className="py-2.5 px-3 font-mono text-xs text-[#6A736B]">{(k.key_preview || '').split('…')[0]}</td>
                    <td className="py-2.5 px-3 text-xs text-[#6A736B]">{fmtDate(k.created_at)}</td>
                    <td className="py-2.5 px-3 text-right font-mono">{k.usage_count}</td>
                    <td className="py-2.5 px-3 text-xs text-[#6A736B]">
                      {k.last_used_at ? (
                        <span className="inline-flex items-center gap-1"><Clock size={12} />{fmtDate(k.last_used_at)}</span>
                      ) : <span className="italic">Nunca</span>}
                    </td>
                    <td className="py-2.5 px-3">
                      {k.revoked ? (
                        <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-red-50 text-red-700 border-red-200">
                          Inactiva
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">
                          Activa
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      {!k.revoked && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-50 h-8"
                          disabled={revokingId === k.id}
                          onClick={() => handleRevoke(k.id, k.name)}
                          data-testid={`api-keys-revoke-${k.id}`}
                        >
                          {revokingId === k.id ? <Loader2 size={14} className="animate-spin mr-1" /> : <Ban size={14} className="mr-1" />}
                          Revocar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Modal: Crear API Key */}
      <Dialog open={createOpen} onOpenChange={(v) => !v && setCreateOpen(false)}>
        <DialogContent className="sm:max-w-md" data-testid="api-keys-create-dialog">
          <DialogHeader>
            <DialogTitle>Crear API Key</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-medium text-[#1A1C18]">Nombre descriptivo</span>
              <Input
                autoFocus
                placeholder="Ej.: TerreMetrics producción"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="mt-1"
                data-testid="api-keys-new-name-input"
              />
            </label>
            <p className="text-xs text-[#6A736B]">
              La clave se mostrará completa <strong>una sola vez</strong>. Guárdala en un lugar seguro antes de cerrar el aviso.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
              className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
              data-testid="api-keys-create-confirm"
            >
              {creating ? <Loader2 size={14} className="animate-spin mr-2" /> : <Plus size={14} className="mr-2" />}
              Generar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: muestra la clave recién creada UNA sola vez */}
      <Dialog open={!!createdKey} onOpenChange={(v) => !v && setCreatedKey(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="api-keys-created-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A1C18]">
              <CheckCircle size={18} className="text-emerald-600" /> API Key creada
            </DialogTitle>
          </DialogHeader>
          {createdKey && (
            <div className="space-y-3">
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-start gap-2">
                <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                <span>
                  Esta es la <strong>única vez</strong> que verás la clave completa.
                  Cópiala ahora — en la lista solo verás los primeros caracteres.
                </span>
              </div>
              <div>
                <div className="text-xs text-[#6A736B] mb-1">Nombre</div>
                <div className="font-semibold text-[#1A1C18]">{createdKey.name}</div>
              </div>
              <div>
                <div className="text-xs text-[#6A736B] mb-1">API Key</div>
                <div className="flex items-stretch gap-2">
                  <code
                    className="flex-1 px-3 py-2 rounded-md border border-[#E8E5DD] bg-[#FDFBF7] text-xs font-mono text-[#1A1C18] break-all"
                    data-testid="api-keys-created-value"
                  >
                    {createdKey.api_key}
                  </code>
                  <Button
                    onClick={copyKey}
                    className="bg-[#1A1C18] hover:bg-black text-white"
                    data-testid="api-keys-copy-btn"
                  >
                    {copied ? <CheckCircle size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                    {copied ? 'Copiada' : 'Copiar'}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCreatedKey(null)} className="bg-[#2B4C3B] hover:bg-[#1E3528] text-white">
              Ya la guardé
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
